<script setup lang="ts">
import ButtonComponent from "@/stories/buttons/Button/Button.vue";
import BaseDialog from "@/stories/dialogs/BaseDialog.vue";
import DaySelectionView from "@/stories/form/DatePicker/DaySelectionView.vue";
import MonthSelectionView from "@/stories/form/DatePicker/MonthSelectionView.vue";
import YearSelectionView from "@/stories/form/DatePicker/YearSelectionView.vue";
import type {
  IUserSelection,
  IViewData,
  ViewType,
} from "@/stories/form/DatePicker/shared";
import { toUnix } from "@/stories/utils/dates";
import { useDates } from "@/stories/utils/useDates";

interface Props {
  isSelecboxOpen: boolean;
  view: IViewData;
  selection: IUserSelection | null;
}

const props = defineProps<Props>();

const emit = defineEmits(["close", "setView", "setSelection", "handleSave"]);

const { format } = useDates();

const VIEW_MAP: Record<ViewType, any> = {
  days: DaySelectionView,
  months: MonthSelectionView,
  years: YearSelectionView,
};

const setView = (value: any) => {
  emit("setView", {
    ...props.view,
    ...value,
  });
};

const setSelection = (selection: any) => {
  const { selection: current } = props;
  if (
    current &&
    current.year === selection.year &&
    current.month === selection.month &&
    current.day === selection.day
  ) {
    emit("setSelection", null);
  } else {
    emit("setSelection", {
      ...props.selection,
      ...selection,
    });
  }
};

const handleSave = () => {
  const { selection } = props;
  if (selection) {
    const date = new Date(selection.year, selection.month, selection.day);
    emit("handleSave", {
      ...selection,
      asString: format("long-date", date),
      asUnix: toUnix(date),
    });
  } else {
    emit("handleSave", null);
  }
};
</script>

<template>
  <BaseDialog v-if="isSelecboxOpen" @on-close="$emit('close')">
    <div
      class="w-[calc(100dvw-2.5rem)] max-w-[26.25rem] desktop:w-[theme(spacing.420)] bg-white rounded-lg"
    >
      <div class="border-b border-neutral300 py-4 px-4">
        <div class="relative flex place-items-center justify-center h-10">
          <ButtonComponent
            type="white"
            size="lg"
            icon="fa-times"
            class="absolute left-0"
            @click="$emit('close')"
          />
          <div class="text-base font-bold">
            {{ $t("dates.chooseDay") }}
          </div>
        </div>
      </div>
      <div class="px-7 pb-4 border-b border-neutral300 w-full">
        <component
          :is="VIEW_MAP[view.type]"
          :view="view"
          :selection="selection"
          @setView="setView"
          @setSelection="setSelection"
        />
      </div>
      <div class="p-4 flex justify-between">
        <ButtonComponent
          :text="$t('global.cancel')"
          size="sm"
          type="white"
          @click="$emit('close')"
        />
        <ButtonComponent
          :text="$t('global.save')"
          size="sm"
          @click="handleSave"
          :disabled="view.type !== 'days'"
        />
      </div>
    </div>
  </BaseDialog>
</template>
